import { useEffect, useState } from 'react';
import {
  getOrganizationSOSSettings,
  getRadiosByOrg,
  Radio,
  updateOrganizationSOSSettings,
} from '../../services/admin';

type RecipientMode = 'dispatcher' | 'radio';

export default function SOSSettingsManager({ orgId }: { orgId: string }) {
  const [radios, setRadios] = useState<Radio[]>([]);
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('radio');
  const [recipientRadioIds, setRecipientRadioIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    getRadiosByOrg(orgId)
      .then((data) => {
        setRadios(data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load radios'))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    setMessage(null);
    setError(null);
    getOrganizationSOSSettings(orgId)
      .then((settings) => {
        setRecipientMode(settings.sos_recipient_mode || 'radio');
        setRecipientRadioIds(settings.sos_recipient_radio_ids || []);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load network SOS settings'));
  }, [orgId]);

  const toggleRecipient = (radioId: string, checked: boolean) => {
    setRecipientRadioIds((current) =>
      checked ? [...current, radioId] : current.filter((id) => id !== radioId)
    );
  };

  const radioLabel = (radio: Radio) => `${radio.radio_id} - ${radio.name || 'Unnamed radio'}`;
  const selectedRadios = radios
    .filter((radio) => recipientRadioIds.includes(radio.id))
    .sort((left, right) => radioLabel(left).localeCompare(radioLabel(right), 'da'));
  const availableRadios = radios
    .filter((radio) => !recipientRadioIds.includes(radio.id))
    .sort((left, right) => radioLabel(left).localeCompare(radioLabel(right), 'da'));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await updateOrganizationSOSSettings(orgId, {
        sos_recipient_mode: recipientMode,
        sos_recipient_radio_ids: recipientRadioIds,
      });
      setMessage('Network SOS recipients saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save network SOS recipients');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-3xl bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white">SOS / Man Down settings</h2>
        <p className="text-sm text-gray-400 mt-1">Configure the recipients for SOS and Man Down alarms in this network.</p>
      </div>

      {loading ? <p className="text-gray-400">Loading radios...</p> : (
        <>
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <p className="text-sm font-semibold text-gray-300">SOS / Man Down recipients</p>
            <div className="flex flex-col gap-3 text-sm text-gray-200">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="sos-recipient-mode"
                  value="radio"
                  checked={recipientMode === 'radio'}
                  onChange={() => setRecipientMode('radio')}
                />
                Selected radios
              </label>
              <label className="flex items-center gap-2 text-gray-500">
                <input
                  type="radio"
                  name="sos-recipient-mode"
                  value="dispatcher"
                  checked={recipientMode === 'dispatcher'}
                  onChange={() => setRecipientMode('dispatcher')}
                />
                Dispatcher (coming later)
              </label>
            </div>
            {recipientMode === 'radio' && (
              <div className="space-y-3">
                <div className="border border-gray-600 rounded divide-y divide-gray-700">
                  {selectedRadios.map((radio) => (
                    <div key={radio.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-gray-200">
                      <span>{radioLabel(radio)}</span>
                      <button
                        type="button"
                        onClick={() => toggleRecipient(radio.id, false)}
                        className="text-red-300 hover:text-red-200 text-xs font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {selectedRadios.length === 0 && <p className="px-3 py-3 text-sm text-gray-400">No radios selected.</p>}
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  disabled={availableRadios.length === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded"
                >
                  Add radio
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500">This is a network-wide setting. The selected radios receive SOS and Man Down alarms from any radio in this network.</p>
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
          {message && <p className="text-sm text-green-400">{message}</p>}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !orgId}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
          >
            {saving ? 'Saving...' : 'Save SOS / Man Down recipients'}
          </button>

          {showAddModal && (
            <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-xl font-bold text-white">Add SOS / Man Down recipient</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-white"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="border border-gray-600 rounded divide-y divide-gray-700">
                  {availableRadios.map((radio) => (
                    <button
                      key={radio.id}
                      type="button"
                      onClick={() => {
                        toggleRecipient(radio.id, true);
                        setShowAddModal(false);
                      }}
                      className="w-full text-left px-3 py-3 text-sm text-gray-200 hover:bg-gray-700"
                    >
                      {radioLabel(radio)}
                    </button>
                  ))}
                  {availableRadios.length === 0 && <p className="px-3 py-3 text-sm text-gray-400">All radios are already selected.</p>}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
